// [PURPOSE]: Fixed tutorial panel + element highlighting for contextual tutorials
// [WHY]: Highlights actual app elements with a cutout glow, shows guidance in a fixed bottom panel
// [DESIGN]: Panel stays in a consistent position (bottom-center). Highlight moves to each target element.
//           User pages through steps with Back/Next. Clean, plain, page-through style.
// [INTEGRATION]: Driven by TutorialContext — renders when activeTutorial is set
// [CREATED]: February 28, 2026 — Contextual Tutorial System redesign

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import { TUTORIAL_MODULES } from '../../data/tutorialModules';
import { X, ChevronRight, ChevronLeft, SkipForward } from 'lucide-react';

// ========================================
// CONSTANTS
// ========================================

const CUTOUT_PADDING = 8;         // px padding around target element
const REPOSITION_DEBOUNCE = 100;  // ms debounce for scroll/resize

// ========================================
// TUTORIAL SPOTLIGHT COMPONENT
// ========================================

const TutorialSpotlight = () => {
  const {
    activeTutorial,
    currentStepIndex,
    getCurrentStep,
    getProgress,
    nextStep,
    prevStep,
    skipStep,
    skipTutorial,
  } = useTutorial();

  // Cutout position state (for highlighting the target element)
  const [cutoutRect, setCutoutRect] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [targetFound, setTargetFound] = useState(false);
  // [F1 FIX]: Hold the last known module/step during fade-out so the component
  // doesn't unmount (return null) before the CSS transition completes.
  const [isFadingOut, setIsFadingOut] = useState(false);
  const fadeOutTimerRef = useRef(null);
  // Cache last valid step data so the fading-out panel has content to render
  const lastStepRef = useRef(null);

  const observerRef = useRef(null);
  const repositionTimerRef = useRef(null);
  const optionalSkipTimerRef = useRef(null);
  // [FIX Phase 2B]: Track whether we ever found the target for the current step.
  // Used to detect when the user navigates the wizard past the current step
  // (target was visible, now it's gone → auto-advance the tutorial).
  const targetSeenRef = useRef(false);

  const currentStep = getCurrentStep();
  // [F1 FIX]: Keep lastStepRef up to date so the fading-out panel has content
  if (currentStep) lastStepRef.current = currentStep;
  const progress = getProgress();

  // ========================================
  // TARGET FINDING + HIGHLIGHT POSITIONING
  // ========================================

  /**
   * Find the nearest scrollable ancestor of an element
   * [WHY]: Elements inside overflow-y-auto containers (like CharacterCreationWizard's
   * race/class grids) have getBoundingClientRect() that extends beyond the visible
   * scroll area. We need to clamp the cutout to the scroll container's visible bounds.
   */
  const findScrollParent = useCallback((el) => {
    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') return parent;
      parent = parent.parentElement;
    }
    return null;
  }, []);

  /**
   * Find the target element and position the highlight cutout
   * [WHY]: Called on step change and when MutationObserver detects new elements.
   *        NOT called from scroll/resize — those use repositionCutout instead.
   */
  const positionHighlight = useCallback(() => {
    if (!currentStep) return;

    const target = document.querySelector(currentStep.target);
    if (!target) {
      setTargetFound(false);
      setCutoutRect(null);

      // [FIX MAR 2026]: Only auto-skip in two cases:
      // 1. Optional step — target may never appear (conditional UI element)
      // 2. Target was previously visible but vanished — user navigated past this step
      // Steps with waitForTarget that we haven't seen yet should WAIT for the
      // MutationObserver to find the target (e.g., wizard page hasn't changed yet).
      if (currentStep.optional || targetSeenRef.current) {
        clearTimeout(optionalSkipTimerRef.current);
        optionalSkipTimerRef.current = setTimeout(() => {
          const recheck = document.querySelector(currentStep.target);
          if (!recheck) skipStep();
        }, currentStep.optional ? 1500 : 500);
      }
      return;
    }

    // Target found — clear any pending skip timer
    clearTimeout(optionalSkipTimerRef.current);
    setTargetFound(true);
    targetSeenRef.current = true;

    // Scroll target into view if needed — check both viewport and scroll container
    const targetRect = target.getBoundingClientRect();
    const panelHeight = 180; // approximate height of the fixed bottom panel

    // Check viewport visibility
    let isInView = (
      targetRect.top >= 0 &&
      targetRect.bottom <= (window.innerHeight - panelHeight) &&
      targetRect.left >= 0 &&
      targetRect.right <= window.innerWidth
    );

    // Also check scroll container visibility (e.g., wizard's overflow-y-auto)
    const scrollParent = findScrollParent(target);
    if (isInView && scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      isInView = (
        targetRect.top >= parentRect.top &&
        targetRect.bottom <= parentRect.bottom
      );
    }

    if (!isInView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Re-position after scroll completes
      setTimeout(() => updateCutout(target), 400);
      return;
    }

    updateCutout(target);
  }, [currentStep, skipStep, findScrollParent]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Update the cutout rectangle around the target element
   * [WHY]: No isVisible guard — setIsVisible(true) is idempotent, and removing the
   *        guard prevents stale closure issues where positionHighlight holds an old
   *        updateCutout that thinks isVisible is already true after step transitions.
   */
  const updateCutout = useCallback((target) => {
    const rect = target.getBoundingClientRect();

    // Clamp rect to the nearest scroll container's visible area
    let top = rect.top;
    let left = rect.left;
    let bottom = rect.bottom;
    let right = rect.right;

    const scrollParent = findScrollParent(target);
    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      top = Math.max(top, parentRect.top);
      left = Math.max(left, parentRect.left);
      bottom = Math.min(bottom, parentRect.bottom);
      right = Math.min(right, parentRect.right);
    }

    const width = right - left;
    const height = bottom - top;

    // Target is completely scrolled out of its container
    if (width <= 0 || height <= 0) {
      setIsVisible(false);
      return;
    }

    setCutoutRect({
      top: top - CUTOUT_PADDING,
      left: left - CUTOUT_PADDING,
      width: width + CUTOUT_PADDING * 2,
      height: height + CUTOUT_PADDING * 2,
    });

    requestAnimationFrame(() => setIsVisible(true));
  }, [findScrollParent]);

  /**
   * Reposition the cutout during user scroll/resize — NO forced scrolling
   * [WHY]: When the user scrolls to see more content (e.g., subrace options below the
   *        race dropdown), we must NOT yank them back to the target. Instead:
   *        - If target is visible: update the cutout position to follow it
   *        - If target is off-screen: hide the overlay so the user can see content freely
   *        - When user scrolls back: the overlay reappears naturally via updateCutout
   *        - If target was visible but is now GONE (wizard navigated): auto-advance
   */
  const repositionCutout = useCallback(() => {
    if (!currentStep) return;

    const target = document.querySelector(currentStep.target);
    if (!target) {
      // [FIX Phase 2B]: If we previously found this target and it's gone,
      // the wizard likely navigated past this step. Auto-advance the tutorial.
      if (targetSeenRef.current) {
        setIsVisible(false);
        setCutoutRect(null);
        clearTimeout(optionalSkipTimerRef.current);
        optionalSkipTimerRef.current = setTimeout(() => {
          const recheck = document.querySelector(currentStep.target);
          if (!recheck) skipStep();
        }, 500);
      }
      return;
    }

    targetSeenRef.current = true;

    const targetRect = target.getBoundingClientRect();

    // [FIX]: Use target CENTER visibility, not just any-pixel-visible.
    // Check both viewport and scroll container bounds.
    const targetCenter = targetRect.top + targetRect.height / 2;
    const panelHeight = 180;
    let isSufficientlyVisible = (
      targetCenter > 0 &&
      targetCenter < (window.innerHeight - panelHeight)
    );

    // Also check scroll container visibility
    const scrollContainer = findScrollParent(target);
    if (isSufficientlyVisible && scrollContainer) {
      const parentRect = scrollContainer.getBoundingClientRect();
      isSufficientlyVisible = (
        targetCenter > parentRect.top &&
        targetCenter < parentRect.bottom
      );
    }

    if (isSufficientlyVisible) {
      updateCutout(target);
    } else {
      // Target scrolled off-screen — hide the overlay so user can browse freely
      setIsVisible(false);
    }
  }, [currentStep, updateCutout, skipStep]);

  // ========================================
  // MUTATION OBSERVER (lazy-loaded elements)
  // ========================================

  useEffect(() => {
    if (!currentStep?.waitForTarget) return;

    observerRef.current = new MutationObserver(() => {
      const target = document.querySelector(currentStep.target);
      if (target) {
        observerRef.current?.disconnect();
        // [FIX]: Clear the "not found" skip timer — target appeared in time
        clearTimeout(optionalSkipTimerRef.current);
        setTimeout(positionHighlight, 200);
      }
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observerRef.current?.disconnect();
  }, [currentStep, positionHighlight]);

  // ========================================
  // SCROLL / RESIZE REPOSITIONING
  // ========================================
  // [FIX]: Uses repositionCutout (no forced scroll) instead of positionHighlight.
  // positionHighlight with scrollIntoView only runs on step change (initial load).
  // This prevents the tutorial from yanking users back when they scroll down
  // to see additional options (subraces, skill selections, equipment choices, etc.)

  useEffect(() => {
    if (!activeTutorial) return;

    const handleReposition = () => {
      clearTimeout(repositionTimerRef.current);
      repositionTimerRef.current = setTimeout(repositionCutout, REPOSITION_DEBOUNCE);
    };

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
      clearTimeout(repositionTimerRef.current);
    };
  }, [activeTutorial, repositionCutout]);

  // ========================================
  // STEP CHANGE → REPOSITION HIGHLIGHT
  // ========================================

  useEffect(() => {
    if (!currentStep) {
      setIsVisible(false);
      setCutoutRect(null);
      return;
    }

    // Reset for transition
    setIsVisible(false);
    setTargetFound(false);
    targetSeenRef.current = false;

    // Clear any pending optional-skip timeout from the previous step
    clearTimeout(optionalSkipTimerRef.current);

    // Let DOM changes settle, then position
    const timer = setTimeout(positionHighlight, 300);
    return () => clearTimeout(timer);
  }, [currentStep, positionHighlight]);

  // ========================================
  // WAIT-FOR-ACTION HANDLER
  // [WHY]: Two strategies — polling for input fields (React synthetic events don't
  //        trigger native 'input' events reliably), native listeners for click/change.
  // ========================================

  useEffect(() => {
    if (!currentStep || currentStep.type !== 'action' || !currentStep.waitFor) return;

    const target = document.querySelector(currentStep.target);
    if (!target) return;

    const eventType = currentStep.waitFor.event;
    let advancedRef = false;

    const tryAdvance = () => {
      if (advancedRef) return;
      if (currentStep.waitFor.validate && !currentStep.waitFor.validate(target)) return;
      advancedRef = true;
      setTimeout(() => nextStep(), 400);
    };

    // For input events on React-controlled fields, poll the DOM value
    // [WHY]: React's onChange dispatches synthetic events — native 'input' listeners
    //        don't fire reliably. Polling the value attribute catches all changes.
    let pollInterval = null;
    if (eventType === 'input') {
      pollInterval = setInterval(() => {
        tryAdvance();
        if (advancedRef && pollInterval) clearInterval(pollInterval);
      }, 300);
    }

    // Also attach native listener as fallback (works for click, change, etc.)
    const handler = () => tryAdvance();
    target.addEventListener(eventType, handler);
    if (eventType === 'input') {
      target.addEventListener('change', handler);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      target.removeEventListener(eventType, handler);
      if (eventType === 'input') {
        target.removeEventListener('change', handler);
      }
    };
  }, [currentStep, nextStep, targetFound]);

  // ========================================
  // KEYBOARD NAVIGATION
  // ========================================

  useEffect(() => {
    if (!activeTutorial) return;

    const handleKeydown = (e) => {
      // [FIX]: Don't capture arrow keys when user is typing in form fields
      const inFormField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName);
      if (e.key === 'Escape') {
        skipTutorial();
      } else if (e.key === 'ArrowRight' && currentStep?.type === 'info' && !inFormField) {
        nextStep();
      } else if (e.key === 'ArrowLeft' && !inFormField) {
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [activeTutorial, currentStep, nextStep, prevStep, skipTutorial]);

  // ========================================
  // [F1 FIX]: FADE-OUT ON TUTORIAL COMPLETE
  // When activeTutorial becomes null, fade out before unmounting
  // ========================================
  useEffect(() => {
    if (!activeTutorial && isVisible) {
      // Tutorial just completed — start fade-out
      setIsVisible(false);
      setIsFadingOut(true);
      clearTimeout(fadeOutTimerRef.current);
      fadeOutTimerRef.current = setTimeout(() => {
        setIsFadingOut(false);
        setCutoutRect(null);
      }, 350); // matches CSS transition-duration: 300ms + buffer
    }
    return () => clearTimeout(fadeOutTimerRef.current);
  }, [activeTutorial, isVisible]);

  // ========================================
  // RENDER
  // ========================================

  // [F1 FIX]: Don't unmount until fade-out animation completes
  if (!activeTutorial && !isFadingOut) return null;
  // During fade-out, use the cached last step so the panel has content while fading
  const renderStep = currentStep || lastStepRef.current;
  if (!renderStep) return null;

  const moduleTitle = TUTORIAL_MODULES[activeTutorial]?.title || TUTORIAL_MODULES[lastStepRef.current?.moduleId]?.title || '';
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = progress.current === progress.total;

  // [WHY]: If the highlighted target is in the bottom half of the screen,
  // show the panel at the TOP so it doesn't overlap the element the user needs to click.
  const targetInBottomHalf = cutoutRect && (cutoutRect.top + cutoutRect.height / 2) > window.innerHeight / 2;
  const panelPosition = targetInBottomHalf ? 'top' : 'bottom';

  return (
    <>
      {/* Dark overlay with cutout hole around the target element */}
      {cutoutRect && (
        <div
          className="fixed z-[150] transition-all duration-300 ease-out"
          style={{
            top: cutoutRect.top,
            left: cutoutRect.left,
            width: cutoutRect.width,
            height: cutoutRect.height,
            borderRadius: '8px',
            boxShadow: isVisible
              ? '0 0 0 9999px rgba(0, 0, 0, 0.3)'
              : '0 0 0 9999px rgba(0, 0, 0, 0)',
            pointerEvents: 'none',
            animation: isVisible ? 'tutorial-highlight 2s ease-in-out infinite' : 'none',
          }}
        />
      )}

      {/* Tutorial panel — positioned top or bottom depending on where the target is */}
      {/* [WHY]: pointer-events: none on the outer wrapper prevents the transparent area
          from blocking clicks on elements behind it (like the wizard's Next button).
          pointer-events: auto on the inner card re-enables interaction with panel buttons. */}
      <div
        className={`fixed z-[151] left-0 right-0 transition-all duration-300 ease-out ${
          panelPosition === 'top' ? 'top-0' : 'bottom-0'
        } ${
          isVisible ? 'opacity-100' : 'opacity-0'
        } ${
          isVisible
            ? (panelPosition === 'top' ? 'translate-y-0' : 'translate-y-0')
            : (panelPosition === 'top' ? '-translate-y-4' : 'translate-y-4')
        }`}
        style={{ pointerEvents: 'none' }}
      >
        <div className="max-w-2xl mx-auto px-4 py-4" style={{ pointerEvents: isVisible ? 'auto' : 'none' }}>
          <div className="bg-gray-900 border border-purple-500/60 rounded-xl shadow-2xl shadow-purple-900/40 overflow-hidden">

            {/* Header — module name + step counter + close */}
            <div className="flex items-center justify-between px-5 py-2.5 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-b border-purple-700/40">
              <span className="text-xs text-purple-300 font-semibold tracking-wide uppercase">
                {moduleTitle}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-purple-400 font-medium">
                  Step {progress.current} of {progress.total}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); skipTutorial(); }}
                  className="text-gray-400 hover:text-white transition-colors p-0.5"
                  title="Close tutorial"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Content — step title + message */}
            <div className="px-5 py-4">
              <h3 className="text-white font-bold text-lg mb-1">
                {renderStep.title}
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                {renderStep.message}
              </p>

              {/* Action hint for wait-for-action steps */}
              {renderStep.type === 'action' && (
                <p className="text-purple-400 text-xs mt-2 italic flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
                  Try it now — the tutorial will advance when you do.
                </p>
              )}

              {/* [FIX APR 2026 Phase 3C.1]: Fail-soft message when the target
                  element can't be found in the DOM. Typically means the element
                  is conditionally-rendered (e.g., downtimes-tracking requires 1+
                  active activity). Previously the spotlight just stayed grey
                  with no explanation. */}
              {!targetFound && !targetSeenRef.current && renderStep.target && (
                <div className="mt-3 bg-amber-900/30 border border-amber-600/50 rounded-lg p-2.5 text-xs text-amber-200">
                  <span className="font-semibold">Heads up:</span> the spotlight
                  couldn&apos;t find what this step highlights — the UI element may
                  only appear in certain states (e.g., after you create something).
                  Read the description above, then use <strong>Next</strong> to continue.
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="px-5 pb-2">
              <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>

            {/* Navigation — Back / Skip / Next */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800">
              {/* Left side: Back + Skip */}
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => { e.stopPropagation(); prevStep(); }}
                  disabled={isFirstStep}
                  className={`text-sm flex items-center gap-1 transition-colors ${
                    isFirstStep
                      ? 'text-gray-600 cursor-not-allowed'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <ChevronLeft size={14} />
                  Back
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); skipTutorial(); }}
                  className="text-gray-500 hover:text-gray-300 text-xs transition-colors flex items-center gap-1"
                >
                  <SkipForward size={11} />
                  Skip All
                </button>
              </div>

              {/* Right side: Next (or Finish) — only for info steps */}
              {currentStep.type === 'info' ? (
                <button
                  onClick={(e) => { e.stopPropagation(); nextStep(); }}
                  className="px-5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 shadow-lg shadow-purple-900/30"
                >
                  {isLastStep ? 'Finish' : 'Next'}
                  <ChevronRight size={14} />
                </button>
              ) : (
                <span className="text-gray-500 text-xs">
                  Use arrow keys to navigate, Esc to skip
                </span>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Pulse animation for the highlight cutout */}
      <style>{`
        @keyframes tutorial-highlight {
          0%, 100% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3),
                        0 0 0 2px rgba(168, 85, 247, 0.4),
                        0 0 12px rgba(168, 85, 247, 0.15);
          }
          50% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3),
                        0 0 0 3px rgba(168, 85, 247, 0.6),
                        0 0 20px rgba(168, 85, 247, 0.3);
          }
        }
      `}</style>
    </>
  );
};

export default TutorialSpotlight;
